import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Callout, Chip, Segmented } from '@/components/ui';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1817 (frame 56): "Đang sửa cho" — scope context above the list table.
 * The toolbar's Phạm vi filter still switches scope; this panel explains what
 * that slice means (wholesale replacement, not inheritance) and, in org scope,
 * surfaces the +/- diff against the default using data already on screen.
 */
export function RoleMapScopePanel({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();

    const orgName =
        vm.actingOrgName ?? t('console.roleMap.scopePanel.orgFallback');

    const scopeOptions = useMemo(() => {
        const options = [];
        if (vm.canManageDefault) {
            options.push({
                label: t('console.roleMap.scopePanel.scopeDefault'),
                value: 'default',
            });
        }
        options.push({
            label: orgName,
            value: 'org',
        });
        return options;
    }, [vm.canManageDefault, orgName, t]);

    const showOrgCallout = vm.scope === 'org' && Boolean(vm.actingOrgId);
    const hasDiffs = vm.scopeOverrideSummaries.length > 0;

    return (
        <section
            className="rm-scope-panel"
            aria-label={t('console.roleMap.scopePanel.sectionAriaLabel')}
        >
            <div className="rm-scope-panel__head">
                <h3 className="rm-scope-panel__title">
                    {t('console.roleMap.scopePanel.title')}
                </h3>
                <Segmented
                    options={scopeOptions}
                    value={vm.scope}
                    onChange={(next) => vm.setScope(next as 'default' | 'org')}
                    aria-label={t('console.roleMap.scopePanel.scopeToggleAriaLabel')}
                />
            </div>

            <p className="rm-scope-panel__intro">
                <Trans
                    i18nKey={
                        vm.scope === 'default'
                            ? 'console.roleMap.scopePanel.introDefault'
                            : 'console.roleMap.scopePanel.introOrg'
                    }
                    values={{ orgName }}
                    components={{ strong: <strong /> }}
                />
            </p>

            {showOrgCallout && (
                <Callout tone="warning" label={t('console.roleMap.scopePanel.orgCalloutLabel')}>
                    <p className="rm-scope-panel__callout-text">
                        <Trans
                            i18nKey="console.roleMap.scopePanel.orgCalloutBody"
                            values={{ orgName }}
                            components={{ strong: <strong /> }}
                        />
                    </p>
                </Callout>
            )}

            {showOrgCallout && hasDiffs && (
                <div className="rm-scope-panel__diff">
                    <h4 className="rm-scope-panel__diff-title">
                        {t('console.roleMap.scopePanel.diffTitle')}
                    </h4>
                    <ul className="rm-scope-panel__diff-list">
                        {vm.scopeOverrideSummaries.map((entry) => (
                            <li key={`${entry.application}::${entry.role_key}`}>
                                <code className="rm-role-key">{entry.role_key}</code>
                                <span className="rm-scope-panel__diff-chips">
                                    {entry.added.map((role) => (
                                        <Chip key={`add-${role}`} tone="mapped">
                                            +{role}
                                        </Chip>
                                    ))}
                                    {entry.removed.map((role) => (
                                        <Chip key={`rem-${role}`} tone="stale">
                                            −{role}
                                        </Chip>
                                    ))}
                                </span>
                                {vm.canManageOrg && (
                                    <button
                                        type="button"
                                        className="rm-scope-panel__diff-action"
                                        onClick={() =>
                                            vm.openScopeDiff({
                                                key: `${entry.application}::${entry.role_key}`,
                                                role_key: entry.role_key,
                                                application: entry.application,
                                                appName: entry.appName,
                                                ids: [],
                                                appRoles: [],
                                                organization_id: vm.actingOrgId,
                                                isStale: false,
                                                accountCount: 0,
                                            })
                                        }
                                    >
                                        {t('console.roleMap.scopePanel.comparePair')}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

export default RoleMapScopePanel;
